import { createFileRoute } from "@tanstack/react-router";
import {
  Container,
  CtaBand,
  DirectAnswer,
  Eyebrow,
  Lead,
  PrimaryLink,
  TextLink,
} from "@/components/site/primitives";
import { SITE } from "@/lib/site-content";
import { ogImageMeta, ogImageUrl } from "@/lib/og";
import { articleSchema, jsonLdScript } from "@/lib/schema";
import { contactFunnel } from "@/lib/analytics";
import { RelatedLinks } from "@/components/site/RelatedLinks";

const PATH = "/non-executive-director-vs-advisor-vs-chair";
const CTA_SOURCE_HERO = "ned_vs_advisor_hero";
const CTA_SOURCE_BAND = "ned_vs_advisor_footer";
const REVIEWED = "5 August 2026";

const title = "Non-Executive Director vs Advisor vs Chair: Duties";
const description =
  "A NED is a statutory director with legal liability, an advisor is not, and the chair runs the board. Know which role you are actually offering.";

const ANSWER =
  "A non-executive director is a registered company director who owes the same statutory duties as executives under the Companies Act 2006, including liability. An advisor holds no office, no vote and no statutory duty. The chair is a director who leads the board itself and is responsible for its effectiveness.";

type Row = { criterion: string; ned: string; advisor: string; chair: string };

const ROWS: Row[] = [
  {
    criterion: "Legal status",
    ned: "Statutory director, registered at Companies House",
    advisor: "No office held, no registration",
    chair: "Statutory director, leading the board",
  },
  {
    criterion: "Duties",
    ned: "General duties under Companies Act 2006, ss.170-177",
    advisor: "Contractual only, as agreed",
    chair: "The same director duties, plus board leadership",
  },
  {
    criterion: "Liability",
    ned: "Same exposure as executive directors; usually covered by D&O insurance",
    advisor: "Limited to the terms of the agreement",
    chair: "Same as any director",
  },
  {
    criterion: "Vote",
    ned: "Votes on board decisions",
    advisor: "None",
    chair: "Votes, and typically holds the casting vote",
  },
  {
    criterion: "Independence",
    ned: "Expected to be independent of the executive",
    advisor: "No independence requirement",
    chair: "Expected to be independent on appointment under the UK Corporate Governance Code",
  },
  {
    criterion: "Primary contribution",
    ned: "Constructive challenge, oversight, assurance to shareholders",
    advisor: "Specialist input on request, no accountability for outcomes",
    chair: "Board composition, agenda, culture and effectiveness",
  },
  {
    criterion: "Right for you when",
    ned: "The board needs a risk it cannot currently challenge to be challenged",
    advisor: "You want expertise without changing governance",
    chair: "The board itself needs leading, not just informing",
  },
];

const FAQ = [
  {
    q: "Is a non-executive director legally a director?",
    a: "Yes. A non-executive director is appointed as a company director and registered at Companies House. Under the Companies Act 2006 the general duties in sections 170 to 177 apply to all directors, so a NED's duties and potential liabilities are the same as an executive director's.",
  },
  {
    q: "Does an advisor carry the same responsibility?",
    a: "No. An advisor holds no office, has no vote and owes no statutory directors' duties. Their obligations are whatever the advisory agreement states. That makes an advisor lighter to appoint, but it also means the board gains input without gaining accountable oversight.",
  },
  {
    q: "What does the chair actually do?",
    a: "The chair leads the board and is responsible for its overall effectiveness. Under the UK Corporate Governance Code the chair should be independent on appointment and should not normally also be the chief executive. Practically, they set the agenda, run the meeting and manage board composition.",
  },
  {
    q: "Can a technology specialist join as an advisor instead of a NED?",
    a: "Often, and sometimes it is the right call. If you want technical scrutiny without changing the board's composition or your filings, an advisory arrangement is simpler. If you need someone who can formally challenge the executive and be accountable for that challenge, that is a NED appointment.",
  },
  {
    q: "Are these roles paid differently?",
    a: "They are structured differently: NED and chair fees are board appointments with formal terms of engagement, while advisory arrangements are contractual and typically narrower. I do not publish fees, because the scope, cadence and liability profile differ too much between engagements to quote a number in advance.",
  },
];

export const Route = createFileRoute("/non-executive-director-vs-advisor-vs-chair")({
  headers: () => ({
    "cache-control": "public, max-age=0, s-maxage=3600, stale-while-revalidate=86400",
  }),
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `${SITE.url}${PATH}` },
      ...ogImageMeta(
        "non-executive-director-vs-advisor-vs-chair",
        "Guide: non-executive director vs advisor vs chair",
      ),
    ],
    links: [{ rel: "canonical", href: `${SITE.url}${PATH}` }],
    scripts: [
      jsonLdScript(
        articleSchema({
          path: PATH,
          headline: title,
          description,
          datePublished: "2026-08-05",
          dateModified: "2026-08-05",
          image: ogImageUrl("non-executive-director-vs-advisor-vs-chair"),
        }),
      ),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE.url },
            {
              "@type": "ListItem",
              position: 2,
              name: "Board and advisory",
              item: `${SITE.url}/board-advisory`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "NED vs advisor vs chair",
              item: `${SITE.url}${PATH}`,
            },
          ],
        }),
      },
    ],
  }),
  component: NedVsAdvisorPage,
});

function NedVsAdvisorPage() {
  return (
    <div>
      <section
        aria-labelledby="nva-hero-heading"
        className="border-b border-border bg-gradient-to-b from-background to-surface"
      >
        <Container>
          <Eyebrow>Guide</Eyebrow>
          <h1
            id="nva-hero-heading"
            className="mt-4 max-w-[24ch] text-[34px] leading-[1.06] font-bold tracking-[-0.035em] text-balance md:text-5xl"
          >
            Non-executive director, advisor or chair?
          </h1>
          <DirectAnswer className="mt-6">{ANSWER}</DirectAnswer>
          <Lead className="mt-6">
            The three are used interchangeably in conversation and are not interchangeable in law.
            The difference decides who can be held to account when something goes wrong.
          </Lead>
          <div className="mt-8">
            <PrimaryLink to="/contact" onClick={() => contactFunnel.ctaClick(CTA_SOURCE_HERO)}>
              Discuss a board appointment
            </PrimaryLink>
          </div>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="nva-table-heading">
        <Container>
          <h2
            id="nva-table-heading"
            className="max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Non-executive director vs advisor vs chair, side by side
          </h2>
          <div className="mt-8 overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[720px] border-collapse text-left text-[15px]">
              <caption className="sr-only">
                Comparison of a non-executive director, an advisor and a chair across seven criteria
              </caption>
              <thead className="bg-surface">
                <tr>
                  <th scope="col" className="border-b border-border px-5 py-4 font-semibold">
                    Criterion
                  </th>
                  <th scope="col" className="border-b border-border px-5 py-4 font-semibold">
                    Non-executive director
                  </th>
                  <th scope="col" className="border-b border-border px-5 py-4 font-semibold">
                    Advisor
                  </th>
                  <th scope="col" className="border-b border-border px-5 py-4 font-semibold">
                    Chair
                  </th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row) => (
                  <tr key={row.criterion} className="align-top">
                    <th
                      scope="row"
                      className="border-b border-border px-5 py-4 font-semibold text-ink"
                    >
                      {row.criterion}
                    </th>
                    <td className="border-b border-border px-5 py-4 text-body">{row.ned}</td>
                    <td className="border-b border-border px-5 py-4 text-body">{row.advisor}</td>
                    <td className="border-b border-border px-5 py-4 text-body">{row.chair}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-5 max-w-[70ch] text-[15px] leading-relaxed text-muted-foreground">
            Legal points reflect UK company law. Governance expectations reflect the UK Corporate
            Governance Code, which applies to companies in the commercial companies or closed-ended
            investment funds categories of the UK Listing Rules, and is widely used as a reference
            by private boards. Judgements about which role fits are my own.
          </p>
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="nva-choose-heading">
        <Container>
          <Eyebrow>How to choose</Eyebrow>
          <h2
            id="nva-choose-heading"
            className="mt-3 max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Which one your board needs
          </h2>
          <div className="mt-6 max-w-[62ch] space-y-5 text-base leading-relaxed text-body">
            <p>
              Start with accountability, not expertise. If the problem is that nobody on the board
              can challenge a technology plan, an advisor does not fix it, an advisor can be
              overruled or simply not invited. A non-executive director sits in the room with a vote
              and a duty.
            </p>
            <p>
              If the problem is narrower, a specific decision, a diligence exercise, a system
              choice, an advisory arrangement is faster to put in place and easier to end, and it
              does not change your governance or your filings.
            </p>
            <p>
              If the board itself is the problem, in composition, agenda or behaviour, that is a
              chair question, and appointing another NED will not resolve it.
            </p>
            <p>
              What a technology NED does in practice, meeting by meeting, is set out in the{" "}
              <TextLink to="/what-a-technology-non-executive-director-does">
                technology NED guide
              </TextLink>
              .
            </p>
          </div>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="nva-faq-heading">
        <Container>
          <Eyebrow>Questions</Eyebrow>
          <h2
            id="nva-faq-heading"
            className="mt-3 text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Common questions
          </h2>
          <dl className="mt-8 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {FAQ.map((item) => (
              <div key={item.q} className="px-6 py-6">
                <dt className="text-lg font-semibold text-ink">{item.q}</dt>
                <dd className="mt-2 max-w-[70ch] text-[15px] leading-relaxed text-body">
                  {item.a}
                </dd>
              </div>
            ))}
          </dl>
          <RelatedLinks
            className="mt-7"
            paths={["/technology-non-executive-director-uk", "/board-advisory"]}
          />
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="nva-sources-heading">
        <Container>
          <h2 id="nva-sources-heading" className="text-xl font-bold tracking-[-0.02em]">
            Sources and review
          </h2>
          <ul className="mt-4 max-w-[74ch] list-disc space-y-2 pl-5 text-[15px] leading-relaxed text-body">
            <li>
              Companies Act 2006, sections 170 to 177, the general duties owed by every director,
              executive and non-executive alike (
              <a
                className="font-semibold underline underline-offset-2 hover:text-primary"
                href="https://www.legislation.gov.uk/ukpga/2006/46/part/10/chapter/2"
                target="_blank"
                rel="noopener noreferrer"
              >
                legislation.gov.uk
              </a>
              ).
            </li>
            <li>
              UK Corporate Governance Code, published by the Financial Reporting Council, board
              leadership, the role of the chair and independence expectations (
              <a
                className="font-semibold underline underline-offset-2 hover:text-primary"
                href="https://www.frc.org.uk/library/standards-codes-policy/corporate-governance/uk-corporate-governance-code/"
                target="_blank"
                rel="noopener noreferrer"
              >
                frc.org.uk
              </a>
              ).
            </li>
            <li>
              Companies House registration of directors, the public record that distinguishes an
              appointed director from an advisor (
              <a
                className="font-semibold underline underline-offset-2 hover:text-primary"
                href="https://www.gov.uk/guidance/being-a-company-director"
                target="_blank"
                rel="noopener noreferrer"
              >
                gov.uk
              </a>
              ).
            </li>
          </ul>
          <p className="mt-4 max-w-[70ch] text-[15px] leading-relaxed text-muted-foreground">
            This page is general information about role types, not legal advice. Take advice on your
            own circumstances before making an appointment.
          </p>
          <p className="mt-4 text-[15px] text-muted-foreground">
            Last reviewed: <time dateTime="2026-08-05">{REVIEWED}</time>
          </p>
        </Container>
      </section>

      <CtaBand
        heading="Deciding what to appoint?"
        body="Tell me what the board cannot currently challenge. I will say whether that calls for a non-executive director, an advisor, or something else entirely."
        to="/contact"
        cta="Discuss a board appointment"
        onCtaClick={() => contactFunnel.ctaClick(CTA_SOURCE_BAND)}
      />
    </div>
  );
}
